// Components
import { useCallback, useMemo, useState } from 'react';
// Types
import { getMenuProps } from 'components/ContextMenu';
import LoadingWrapper from 'components/LoadingWrapper';
import { useDefaultTableProps } from 'hooks/useDefaultTableProps';
import { useDeleteConfirmation } from 'hooks/useDeleteConfirmation';
// Utils
import { useRowProps } from 'hooks/useRowProps';
import { useTableTitle } from 'hooks/useTableTitle';
import { getText } from 'i18n/intl';
// Slices
import { useAppDispatch, useAppSelector } from 'slices/store';
import {
  checkFirstTimeZoneExists,
  createUnsavedTimeZone,
  deleteTimeZone,
  dstMapSelector,
  dstsSelector,
  duplicateTimeZone,
  selectedTimeZoneIdsSelector,
  setFirstTimeZone,
  setSecondTimeZone,
  timeZoneLoadingSelector,
  timeZoneMapSelector,
  timeZonesSelector,
  zoneMapSelector,
  zonesSelector,
} from 'slices/timeZone.slice';
import { matchString } from 'utils/stringUtils';
import type { TDst, TTimeZone, TZone } from '@timeedit/types/lib/types';
import {
  Button,
  type Column,
  EditableTable,
  Filter,
  TEEFilterType,
  type TEIFilter,
  type TEIFilterField,
} from '@timeedit/ui-components';
import { Space } from '@timeedit/ui-components/antd';
// Styles
import './TimeZoneList.scss';

interface IRowData {
  id: number;
  name: string;
  extId: string;
  timeZone: string;
  offset: string;
  dst: string;
  identifier: string;
}

interface Props {
  confirmDiscardPaddingChanges: (
    isFirstDrawer: boolean,
    actionFn: () => void
  ) => void;
}

const TimeZoneList = ({ confirmDiscardPaddingChanges }: Props) => {
  const dispatch = useAppDispatch();

  const timeZones: TTimeZone[] = useAppSelector(timeZonesSelector);
  const timeZoneMap: Record<number, TTimeZone> =
    useAppSelector(timeZoneMapSelector);
  const selectedIds: number[] = useAppSelector(selectedTimeZoneIdsSelector);
  const zones = useAppSelector(zonesSelector);
  const zoneMap = useAppSelector(zoneMapSelector);
  const dsts = useAppSelector(dstsSelector);
  const dstMap = useAppSelector(dstMapSelector);

  const loading = useAppSelector(timeZoneLoadingSelector);

  const [filterSettings, setFilterSettings] = useState<TEIFilter>({
    filters: {},
    freeTextSearch: '',
  });

  const [confirmDelete, contextHolder] = useDeleteConfirmation();

  const options = useMemo(
    () => ({
      timeZone: zones.map((zone: TZone) => ({
        value: zone.name,
        label: zone.name,
      })),
      dst: dsts.map((dst: TDst) => ({
        value: dst.name,
        label: dst.name,
      })),
    }),
    [zones, dsts]
  );

  const filterData: IRowData[] = useMemo(() => {
    const dataSource = timeZones.map(
      (timeZone) =>
        ({
          id: timeZone.id,
          name: timeZone.name ? timeZone.name : getText('new_time_rule'),
          extId: timeZone.extId,
          timeZone: zoneMap[timeZone.timeZone]?.name,
          dst: dstMap[timeZone.daylightSavingsTime]?.name,
          offset: timeZone.offset,
          identifier: timeZone.identifiers?.join(', ') || 'None',
        }) as IRowData
    );

    return dataSource.filter((item) => {
      let matchedText = true;
      let zoneMatched = true;
      let dstMatched = true;
      const {
        freeTextSearch,
        filters: { timeZone, dst },
      } = filterSettings;
      if (freeTextSearch) {
        matchedText = matchString(item.name, freeTextSearch);
      }
      if (timeZone) {
        zoneMatched = matchString(item.timeZone, timeZone as string);
      }
      if (dst) {
        dstMatched = matchString(item.dst, dst as string);
      }
      return matchedText && zoneMatched && dstMatched;
    });
  }, [timeZones, filterSettings]);

  const onCreate = useCallback(() => {
    confirmDiscardPaddingChanges(true, () => dispatch(createUnsavedTimeZone()));
  }, []);

  const onDuplicate = useCallback(
    (record: IRowData) => {
      confirmDiscardPaddingChanges(true, () =>
        dispatch(duplicateTimeZone(timeZoneMap[record.id]))
      );
    },
    [timeZoneMap]
  );

  const onCompare = useCallback(
    (record: IRowData) => {
      const firstTimeZoneExist = dispatch(checkFirstTimeZoneExists());
      if (!firstTimeZoneExist) {
        return dispatch(setFirstTimeZone(timeZoneMap[record.id]));
      }
      confirmDiscardPaddingChanges(false, () =>
        dispatch(setSecondTimeZone(timeZoneMap[record.id]))
      );
    },
    [timeZoneMap]
  );

  const onRowClick = useCallback(
    ({ id }: IRowData) => {
      confirmDiscardPaddingChanges(true, () =>
        dispatch(setFirstTimeZone(timeZoneMap[id]))
      );
    },
    [timeZoneMap]
  );

  const onRow = useRowProps<IRowData>(onRowClick, selectedIds);

  const onDelete = useCallback(
    (record: IRowData) =>
      confirmDelete(record.name, () => dispatch(deleteTimeZone(record.id))),
    [confirmDelete, dispatch]
  );

  const checkDrawerOpen = useCallback(
    () => Boolean(dispatch(checkFirstTimeZoneExists())),
    [dispatch]
  );
  const menuProps = useCallback(
    (record: IRowData) =>
      getMenuProps<IRowData>({
        record,
        onCompare,
        onDuplicate,
        onDelete,
        checkDrawerOpen,
        title: record.name,
      }),
    [onCompare, onDuplicate, onDelete, checkDrawerOpen]
  );

  const footer = useCallback(
    () => (
      <Space>
        <Button size="small" type="primary" onClick={onCreate}>
          Create new
        </Button>
      </Space>
    ),
    [onCreate]
  );

  const title = useTableTitle<IRowData>({
    total: timeZones.length,
    filteredAmount: filterData.length,
  });

  const [contentRef, defaultTableProps] = useDefaultTableProps();

  return (
    <div className="time-zone-list" ref={contentRef}>
      <Filter
        defaultFields={defaultFields}
        options={options}
        settings={filterSettings}
        onChange={setFilterSettings}
      />
      <LoadingWrapper isLoading={loading}>
        <EditableTable
          dataSource={filterData}
          columns={columns}
          getMenuProps={menuProps}
          onRow={onRow}
          footer={footer}
          title={title}
          {...defaultTableProps}
          rowKey="key"
        />
      </LoadingWrapper>
      {contextHolder}
    </div>
  );
};

export default TimeZoneList;

const columns: Column<IRowData>[] = [
  {
    key: 'name',
    dataIndex: 'name',
    title: 'Name',
    sorter: (item1: IRowData, item2: IRowData) =>
      item1.name.localeCompare(item2.name),
    fixed: 'left',
  },
  {
    key: 'extId',
    dataIndex: 'extId',
    title: 'ExtId',
    sorter: (item1: IRowData, item2: IRowData) =>
      item1.extId?.localeCompare(item2.extId),
    fixed: 'left',
  },
  {
    key: 'timeZone',
    dataIndex: 'timeZone',
    title: 'Time Zone',
    sorter: (item1: IRowData, item2: IRowData) =>
      item1.timeZone.localeCompare(item2.timeZone),
  },
  {
    key: 'dst',
    dataIndex: 'dst',
    title: 'Daylight Savings Time',
    sorter: (item1: IRowData, item2: IRowData) =>
      item1.dst.localeCompare(item2.dst),
  },
  {
    key: 'offset',
    dataIndex: 'offset',
    title: 'Offset',
    sorter: (item1: IRowData, item2: IRowData) =>
      item1.offset.localeCompare(item2.offset),
  },
  {
    key: 'identifier',
    dataIndex: 'identifier',
    title: 'Identifiers',
    sorter: (item1: IRowData, item2: IRowData) =>
      item1.identifier.localeCompare(item2.identifier),
  },
];

const defaultFields: TEIFilterField[] = [
  {
    key: 'timeZone',
    label: 'Time Zone',
    type: TEEFilterType.text,
  },
  {
    key: 'dst',
    label: 'Daylight Savings Time',
    type: TEEFilterType.text,
  },
];
